import type {
	ResultAndError,
	ResultOrError,
	Timestamp,
} from '@marmon/shared/types/common';
import {
	numberToCents,
	numberToPercentage,
	roundNumber,
} from '@marmon/shared/types/common';
import { ParseResult, unparse } from 'papaparse';
import type {
	Analysis,
	CalculationId,
	Margin,
	ParseInvoiceError,
	RawInvoice,
	TimePeriodProfit,
} from '../interfaces';
import { DefaultCalculations, RawInvoiceKeys } from '../interfaces';
import { isEmpty } from './invoice-formatter.utils';
import { FormatRawInvoiceResult, formatRawInvoices } from './invoice.utils';

/**
 * Extra columns are allowed. PapaParse reports them as FieldMismatch when a
 * row is wider or shorter than the header list; we still have the required
 * keys after `pickRequiredRawInvoiceColumns`.
 */
function isIgnorableExtraColumnError(error: ParseInvoiceError): boolean {
	return (
		error.type === 'FieldMismatch' &&
		(error.code === 'TooManyFields' || error.code === 'TooFewFields')
	);
}

/**
 * Excel often prefixes the first header with a BOM, and exports may pad names
 * with spaces. Matching is by this normalized name, not column position.
 */
export function normalizeInvoiceHeader(header: string): string {
	return header.replace(/^\uFEFF+/, '').trim();
}

/**
 * Copies only the required invoice columns from a parsed CSV row.
 * Extra headers can appear before, between, or after required ones; lookup is
 * by normalized header name, not order.
 */
export function pickRequiredRawInvoiceColumns(row: RawInvoice): RawInvoice {
	const values = new Map<string, string>();
	for (const [header, value] of Object.entries(row)) {
		const normalized = normalizeInvoiceHeader(header);
		if (!normalized || values.has(normalized)) continue;
		values.set(normalized, value ?? '');
	}

	return RawInvoiceKeys.reduce((picked, key) => {
		picked[key] = values.get(key) ?? '';
		return picked;
	}, {} as RawInvoice);
}

/**
 * PapaParse `skipEmptyLines: true` still emits comma-only rows (`,,,,`).
 * `greedy` drops those so trailing Excel blank rows are not invoices.
 */
export const InvoiceCsvSkipEmptyLines = 'greedy' as const;

const RequiredInvoiceValueKeys = [
	'Customer',
	'Part #',
	'Revenue',
	'Invoice Date',
	'Cost',
] as const;

/**
 * True when the row has no invoice payload. Segment may be empty on a real
 * row; a row with every other required field blank is leftover CSV padding.
 */
export function isBlankRawInvoiceRow(row: RawInvoice): boolean {
	return RequiredInvoiceValueKeys.every((key) => isEmpty(row[key]));
}

/**
 * Builds a CSV string that contains only `RawInvoiceKeys`, in that order.
 * Used so Storage keeps a canonical file even when the upload had extras.
 */
export function rawInvoicesToCsv(rows: RawInvoice[]): string {
	return unparse(rows.map(pickRequiredRawInvoiceColumns), {
		columns: [...RawInvoiceKeys],
		header: true,
		newline: '\n',
	});
}

/**
 * Evaluates all of the results that are returned from PapaParse. We need to provide the
 * results here because depending on where Papa Parse is ran (client v server) we need to
 * run different logic streams and provide different values.
 *
 * Extra columns are ignored. Required `RawInvoiceKeys` must still be present.
 *
 * @param result the parsed results from papa parse
 * @returns errors or results based on the results provided
 */
export function evaluateParsedResults(
	result: Pick<ParseResult<RawInvoice>, 'data' | 'errors' | 'meta'>,
): ResultAndError<{ data: RawInvoice[] }, ParseInvoiceError> {
	if (!result)
		throw Error('The source could not be parsed, result returned nullish');

	const errors: ParseInvoiceError[] = [];
	const { aborted } = result.meta;
	const fields = (result.meta.fields ?? []).map(normalizeInvoiceHeader);
	// Check for errors that prevent the parsing to complete
	if (aborted) {
		errors.push({
			code: 'Aborted',
			type: 'Unknown',
			message:
				'Operation Aborted: The operation could not be finished, please fix all errors and try again.',
		});
	}

	// Required headers must exist by name, in any order. Extra headers in any
	// position are ignored until we pick required columns for Storage.
	if (!fields.length) {
		errors.push({
			code: 'TooFewFields',
			type: 'FieldMismatch',
			message:
				'Too few fields: No column headers could be determined in the CSV.',
		});
	} else {
		RawInvoiceKeys.forEach((f, index) => {
			if (fields.includes(f)) return;
			errors.push({
				code: 'MissingField',
				type: 'FieldMismatch',
				message: `Field missing: The expected field "${f}" was missing from the CSV.`,
				messageHtml: `Field missing: The expected field "<b>${f}</b>" was missing from the CSV.`,
				row: 0,
				index,
			});
		});
	}

	if (result.errors?.length) {
		errors.push(
			...result.errors.filter(
				(error) => !isIgnorableExtraColumnError(error),
			),
		);
	}

	return {
		data: result.data
			.map(pickRequiredRawInvoiceColumns)
			.filter((row) => !isBlankRawInvoiceRow(row)),
		errors,
	};
}

/**
 * Parses raw CSV rows into invoice documents.
 *
 * @param options.csvIndexes Pass through from `parseSource` so filtered CSV
 * rows keep their original indexes for `createInvoiceId()`. Omit only when
 * `data` is still full-file order (import with no segment filter).
 */
export function formatRawInvoiceData(
	rawInvoiceData: ResultAndError<{ data: RawInvoice[] }, ParseInvoiceError>,
	options: { now: Timestamp; csvIndexes?: number[] },
): ResultOrError<FormatRawInvoiceResult, ParseInvoiceError> {
	// initial parsing errors need to be fixed before creating invoice data items
	if (rawInvoiceData.errors.length) return { errors: rawInvoiceData.errors };

	const { now, csvIndexes } = options;
	const data: RawInvoice[] = [];
	const keptCsvIndexes: number[] = [];
	rawInvoiceData.data.forEach((row, i) => {
		const picked = pickRequiredRawInvoiceColumns(row);
		if (isBlankRawInvoiceRow(picked)) return;
		data.push(picked);
		if (csvIndexes) keptCsvIndexes.push(csvIndexes[i]);
	});

	const invoiceData = formatRawInvoices(data, {
		now,
		csvIndexes: csvIndexes ? keptCsvIndexes : undefined,
	});

	// return only errors if the invoices could not be created properly
	if (invoiceData.errors) return { errors: invoiceData.errors };

	return { result: invoiceData.result };
}

/**
 * Calculates and generates an object of the margin for the provided
 * revenue and direct cost
 *
 * @note that the cost MUST be `Direct` and cannot work with `Material`. If the invoice
 * is marked as a `Material Cost` invoice then contribution margin will not be reliable.
 *
 * @note the custom comparisons and returns are directly from the calculations of how to
 * obtain these values.
 * - revenue and cost are 0, then the value is 0 and the percentage cannot be determined
 * - cost is 0, then the value is the revenue and the percentage is 100%
 * - revenue is 0, then the value is essentially the negative cost and percentage cannot
 * 		be determined
 * - otherwise calculate as normal
 *
 * @note CM$ CAN be negative, and it is actually "common". It occurs when the revenue is
 * less than the cost.
 *
 * @param revenue the revenue that of the invoice
 * @param cost the direct cost of the invoice
 * @returns a newly generated contribution margin object
 */
export function generateMargin(revenue: number, cost: number): Margin {
	const valueInCents = revenue - cost;
	const percentage = valueInCents / revenue;

	// percentage needs to be a real and finite number
	const invalid = !Number.isFinite(percentage) || Number.isNaN(percentage);
	return {
		valueInCents: numberToCents(valueInCents),
		percentage: invalid ? null : numberToPercentage(percentage),
	};
}

/**
 * Determines what the rate at which the new value changed in comparison to the old value.
 * @param first the old value
 * @param second the new value
 * @returns the percent that the value had changed
 */
export const percentChange = (first: number, second: number) =>
	(second - first) / first;

/**
 * Calculates what the new cost should be based on the the previous values combined with
 * what the new marginPercent should be after adjustments have been made.
 *
 * @note some calculation steps to help with the workflow
 *     M$ = R$ - C$
 *     M% = M$ / R$
 *     M$ = M% * R$
 *     oM$ - M$ = Rd$
 *     oR$ + Rd$ = R$
 *
 * @param prev the previous values to help calculate the new ones
 * @param marginPercentNew the new margin percent to apply to the old values
 * @returns
 */
export function generateCost(revenueInCents: number, marginPercent: number) {
	const marginInCents = revenueInCents * marginPercent;
	return roundNumber(revenueInCents - marginInCents);
}

/**
 * Calculation to get the margin modifier to apply onto the revenue in order
 * to get what the revenue value should be when:
 *
 * @calculation
 * Mm% = ((C$ / (1 - Mn%) - R$) / R%)
 * revenueInCentsNew = R$ * Mm%
 */
export function generateRevenue(
	prev: TimePeriodProfit,
	marginPercentNew: number,
) {
	const { costInCents, revenueInCents } = prev;
	const mod =
		(costInCents / (1 - marginPercentNew) - revenueInCents) /
		revenueInCents;
	return roundNumber(revenueInCents * (mod + 1));
}

/**
 * Any cast the default calculation
 */
export const isDefaultCalculation = (id: CalculationId) =>
	DefaultCalculations.includes(id);

/**
 * Aggregates the overhead costs for the time periods and returns the total
 *
 * @param analysis the analysis to gather the total overhead from
 */
export const totalOverheadInCents = (analysis: Analysis) =>
	analysis.timePeriods.reduce((v, c) => v + c.overheadCostInCents, 0);
